import { fireImpression, getVariation, whenAvailable } from "@vp/ab-reader";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { IdentityContext } from "./IdentityProvider";
import { PageStaticDataContext } from "./PageStaticDataContextProvider";
// @ts-ignore
import { PluginExperimentContext } from "../pluginsInfrastructure/PluginRegistryTypes";
import { analyticsPageData, analyticsPageName } from "../utils/analyticsConfig";

interface ExperimentsContextProps {
  useExperiment: (context: PluginExperimentContext) => string | undefined;
  isExperimentInitialized: boolean;
}

const ExperimentsContext = createContext<ExperimentsContextProps>({
  useExperiment: () => undefined,
  isExperimentInitialized: false,
});

const ExperimentsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isExperimentInitialized, setIsExperimentInitialized] = useState(false);
  const { isIdentityInitialized } = useContext(IdentityContext);
  const { locale, mpv } = useContext(PageStaticDataContext);
  const firedImpressions = useRef<string[]>([]);
  const { mpvId } = mpv;

  useEffect(() => {
    let mounted = true;
    whenAvailable(() => {
      if (mounted) {
        setIsExperimentInitialized(true);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  const useExperiment = useCallback(
    (context: PluginExperimentContext) => {
      const {
        experimentKey,
        renderTestExperienceForVariations,
        supportedLocales,
        supportedMpvIds,
        fireImpressionForVariations,
      } = context;
      if (isExperimentInitialized && experimentKey) {
        const variationKey = getVariation(experimentKey) || undefined;

        if (!variationKey) return undefined;

        const commonCriteriaMet =
          !!variationKey &&
          (supportedLocales ? supportedLocales.includes(locale) : true) &&
          (supportedMpvIds ? supportedMpvIds.includes(mpvId) : true);

        const renderTestExperienceCriteriaMet: boolean =
          commonCriteriaMet && renderTestExperienceForVariations.includes(variationKey);

        const fireImpressionCriteriaMet: boolean =
          commonCriteriaMet && fireImpressionForVariations.includes(variationKey);

        if (fireImpressionCriteriaMet && isIdentityInitialized) {
          // Fire an impression as long as variation key is defined so that we track both control and test experience.
          if (!firedImpressions.current.includes(`${experimentKey}_${variationKey}`)) {
            firedImpressions.current.push(`${experimentKey}_${variationKey}`);
            fireImpression(experimentKey, variationKey, {
              pageName: analyticsPageName(mpvId),
              pageSection: analyticsPageData.pageSection,
              pageStage: analyticsPageData.pageStage,
            });
          }
        }

        return renderTestExperienceCriteriaMet ? variationKey : undefined;
      }

      return undefined;
    },
    [isExperimentInitialized, isIdentityInitialized]
  );

  return (
    <ExperimentsContext.Provider
      value={{
        useExperiment: useExperiment,
        isExperimentInitialized,
      }}
    >
      {children}
    </ExperimentsContext.Provider>
  );
};

const useExperimentsContext = () => {
  return useContext(ExperimentsContext);
};

export { useExperimentsContext };
export default ExperimentsProvider;
